import React, { useState, useEffect, useRef } from 'react';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import IconBack from 'cccisd-icons/arrow-left8';
import IconStar from 'cccisd-icons/star-half';
import IconReport from 'cccisd-icons/file-text';
import Loader from 'cccisd-loader';
import Modal from 'cccisd-modal';
import Table from 'cccisd-table';
import reportTemplates from 'js/vendor/reports/reportTemplates/index.js';
import query from './progress.graphql';

import { TFH_DEPLOYMENT_HANDLE } from '../../TfhProgram';

const reportList = _get(window, 'cccisd.appDefs.reports.list', []);

const Progress = ({ family, familyName, isStartOpen }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);
    const [currReport, setCurrReport] = useState(null);
    const [deploymentId, setDeploymentId] = useState(null);
    const modalRef = useRef(null);

    const familyPawnId = family?.descendantRoles?.metricspawn?.pawn?.pawnId || -1;
    const familyPawnHash = family?.descendantRoles?.metricspawn?.pawn?.pawnHash || '';

    useEffect(() => {
        async function handleScroll() {
            // give a second for the report to load
            await new Promise(res => setTimeout(res, 1000));

            const el = document.getElementById(currReport.lessonId);
            if (!el) {
                return;
            }

            el.scrollIntoView({ behavior: 'smooth' });
        }

        if (currReport?.lessonId) {
            handleScroll();
        }
    }, [currReport]);

    useEffect(() => {
        if (isStartOpen && modalRef.current) {
            modalRef.current.open();
        }
    }, [isStartOpen, modalRef]);

    async function getData() {
        setIsLoading(true);
        const resp = await client.query({
            query,
            variables: {
                metricsPawnId: familyPawnId,
                metricsPawnHash: familyPawnHash,
                deploymentHandle: TFH_DEPLOYMENT_HANDLE,
            },
            fetchPolicy: 'network-only',
        });

        const depId = _get(resp, 'data.flows.deployment.deploymentId', null);
        if (!depId) {
            console.error('No deployment found with handle ' + TFH_DEPLOYMENT_HANDLE);
            return;
        }
        setDeploymentId(depId);

        const surveyProgress = {};

        const allSurveys = resp.data.flows?.deployment?.assignment?.surveyList || [];
        allSurveys.forEach(surv => {
            if (!surv.label.match(/^M[\d]{1,}L[\d]{1,}/) && !surv.label.match(/Parent\sModule\sQuiz/)) {
                return;
            }

            surveyProgress[surv.surveyHandle] = {};
            surveyProgress[surv.surveyHandle].label = surv.label;
            surveyProgress[surv.surveyHandle].position = surv.position;

            const moduleMatch = surv.label.match(/^M[\d]{1,}/);
            if (Array.isArray(moduleMatch) && moduleMatch.length > 0) {
                surveyProgress[surv.surveyHandle].module = parseInt(moduleMatch[0].replace('M', ''), 10);
            }

            const lessonMatch = surv.label.match(/^M[\d]{1,}L([\d]{1,})/);
            const quizMatch = surv.label.match(/Parent\sModule\sQuiz/);
            if (Array.isArray(lessonMatch) && lessonMatch.length > 1) {
                surveyProgress[surv.surveyHandle].lesson = parseInt(lessonMatch[1], 10);
            } else if (Array.isArray(quizMatch) && quizMatch.length > 0) {
                surveyProgress[surv.surveyHandle].lesson = 'quiz';
            }
        });

        const allVariables = resp.data.roles?.metricspawn?.assignmentProgress?.variables || {};
        for (let [key, val] of Object.entries(allVariables)) {
            if (!key.startsWith('survey')) {
                continue;
            }

            let handleMatch = key.match(/^survey[0-9]{1,}/);
            if (!handleMatch || !Array.isArray(handleMatch)) {
                continue;
            }

            const handle = handleMatch[0];
            if (!(handle in surveyProgress)) {
                continue;
            }

            const subKey = key.slice(key.lastIndexOf('_') + 1);
            if (subKey in surveyProgress[handle]) {
                console.error('Yikes! Key ' + subKey + ' is already in progress map');
                continue;
            }

            surveyProgress[handle][subKey] = val;
        }

        setData(surveyProgress);
        setIsLoading(false);
    }

    function renderReport() {
        let report = reportList.find(item => item.id === currReport.handle);
        let reportTemplate = report && reportTemplates.find(item => item.handle === report.reportTemplateHandle);

        return (
            <div>
                <button className="btn btn-lg btn-link ps-0 mb-4" type="button" onClick={() => setCurrReport(null)}>
                    <IconBack spaceRight />
                    Back to {familyName}
                </button>
                {reportTemplate ? (
                    <reportTemplate.player
                        settings={report.reportTemplateSettings}
                        widgetProps={{
                            pawnId: familyPawnId,
                            pawnHash: familyPawnHash,
                            deploymentId,
                        }}
                        variables={{}}
                    />
                ) : (
                    <div className="alert alert-warning">
                        <p className="mb-0">
                            Report not created for progress in module <b>{currReport.handle}</b>.
                        </p>
                    </div>
                )}
            </div>
        );
    }

    const trigger = (
        <button type="button" className="btn btn-sm btn-secondary">
            <IconStar spaceRight />
            Progress
        </button>
    );

    return (
        <Modal
            ref={modalRef}
            title={'Progress - ' + familyName}
            trigger={trigger}
            beforeShow={getData}
            afterClose={() => setCurrReport(null)}
            size="large"
            render={({ closeModal }) => {
                if (isLoading || !data) {
                    return <Loader loading />;
                }

                if (currReport) {
                    return renderReport();
                }

                return (
                    <div>
                        <Table
                            rowKey="handle"
                            saveState={false}
                            showPerPageOptions={false}
                            perPage={50}
                            name={'progress_' + family.group.groupId}
                            columns={[
                                {
                                    name: 'label',
                                    label: 'Lesson',
                                    sort: true,
                                    filter: true,
                                    sortSettings: {
                                        custom(row) {
                                            return `${row.module}${row.lesson}`;
                                        },
                                    },
                                },
                                {
                                    name: 'start',
                                    label: 'Started',
                                    sort: true,
                                    filter: true,
                                    filterSettings: { type: 'date' },
                                },
                                {
                                    name: 'end',
                                    label: 'Completed',
                                    sort: true,
                                    filter: true,
                                    filterSettings: { type: 'date' },
                                },
                                {
                                    name: 'lastVisited',
                                    label: 'Last Visited',
                                    sort: true,
                                    filter: true,
                                    filterSettings: { type: 'date' },
                                },
                                {
                                    name: 'report',
                                    label: 'Report',
                                    render: (value, row) => {
                                        if (!row.module || !row.lesson || !row.start) {
                                            return <span />;
                                        }

                                        return (
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={() =>
                                                    setCurrReport({
                                                        handle: 'tfhModule' + row.module,
                                                        lessonId: 'L' + row.lesson,
                                                    })
                                                }
                                            >
                                                <IconReport />
                                            </button>
                                        );
                                    },
                                },
                            ]}
                            data={Object.keys(data).map(handle => {
                                const vals = data[handle];
                                return {
                                    handle,
                                    ...vals,
                                };
                            })}
                        />
                        <div className="mt-4">
                            <button className="btn btn-secondary" type="button" onClick={() => closeModal()}>
                                Close
                            </button>
                        </div>
                    </div>
                );
            }}
        />
    );
};

export default Progress;
